import LegalPage, { Section } from "@/components/LegalPage";

const UPDATED = "June 1, 2026";

export default function Privacy() {
  return (
    <LegalPage eyebrow="PRIVACY POLICY" title="Your inbox is yours. We only look for receipts." updated={UPDATED} testid="privacy-page">
      <p>
        Clawback ("Clawback", "we", "us") operates <a href="https://refundbot.org" className="text-[#00E676]">refundbot.org</a> and the Clawback
        web application (the "Service"). This policy explains what we collect, why, and the choices you have. By using the
        Service you agree to this policy.
      </p>

      <Section title="1. Information we collect">
        <ul className="list-disc pl-5 space-y-2">
          <li><b className="text-[#F4F1EA]">Account data</b> — your email address, name (optional) and a salted password hash. We never store plain-text passwords.</li>
          <li><b className="text-[#F4F1EA]">Gmail data (only if you connect Gmail)</b> — with your explicit consent via Google OAuth, we read messages that match receipt, invoice, subscription and trial keywords (sender, subject, date, snippet and body). From those we extract merchant name, amount, currency, billing interval, trial status and next-charge date. We store the extracted charge records and the Gmail message ID used for de-duplication. We do <u>not</u> store full copies of your emails.</li>
          <li><b className="text-[#F4F1EA]">Recovery data</b> — the cancellations, refund requests, class-action claims and their status that you create in the app.</li>
          <li><b className="text-[#F4F1EA]">Payment data</b> — handled entirely by Stripe. We store your Stripe customer ID, subscription ID and plan; we never see or store card numbers.</li>
          <li><b className="text-[#F4F1EA]">Technical data</b> — IP address, browser type and basic usage logs needed to run and secure the Service.</li>
        </ul>
      </Section>

      <Section title="2. How we use Google user data">
        <p>
          Clawback's use and transfer of information received from Google APIs adheres to the{" "}
          <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noreferrer" className="text-[#00E676]">Google API Services User Data Policy</a>, including the Limited Use requirements.
        </p>
        <p>We request the following Google scopes and use them solely to provide user-facing features you request:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li><code className="font-mono text-xs text-[#F4F1EA]">gmail.readonly</code> — to find receipt and subscription emails and extract charge details, which we display to you as your "leaks".</li>
          <li><code className="font-mono text-xs text-[#F4F1EA]">gmail.send</code> — to send the nightly spending brief <i>from your own Gmail address to yourself</i>, only when you keep that option enabled. We never send email to anyone else on your behalf.</li>
          <li><code className="font-mono text-xs text-[#F4F1EA]">openid / email</code> — to confirm which Google account you connected.</li>
        </ul>
        <p>We do <b className="text-[#F4F1EA]">not</b>:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Sell Google user data, or use it for advertising or building marketing profiles.</li>
          <li>Transfer Google user data to third parties, except as necessary to provide the feature (AI receipt parsing described below), to comply with law, or as part of a merger/acquisition with notice to you.</li>
          <li>Allow humans to read your Gmail data, except with your explicit permission for support, when required for security or legal reasons, or when aggregated and anonymized.</li>
          <li>Use Google user data to develop, improve or train generalized AI or machine-learning models.</li>
        </ul>
      </Section>

      <Section title="3. AI processing of receipts">
        <p>
          To identify charges, the text of matching receipt emails is sent to a large-language-model provider (Anthropic Claude, via an API processor)
          strictly to extract structured fields (merchant, amount, interval, trial status). The provider processes the text transiently under contractual
          terms that prohibit using it for model training. We also use the same provider to generate refund letters, cancellation scripts and your
          spending brief from the extracted records.
        </p>
      </Section>

      <Section title="4. How we share information">
        <ul className="list-disc pl-5 space-y-2">
          <li><b className="text-[#F4F1EA]">Stripe</b> — payments and subscription management.</li>
          <li><b className="text-[#F4F1EA]">Google</b> — OAuth and Gmail API access you authorize.</li>
          <li><b className="text-[#F4F1EA]">AI processor</b> — transient parsing described above.</li>
          <li><b className="text-[#F4F1EA]">Hosting and database providers</b> — to store and serve the Service.</li>
          <li><b className="text-[#F4F1EA]">Recovery Wall</b> — if a refund you tracked lands, we may show an anonymized entry (first initial, merchant and amount) on our public wall. No email, name or account details are shown.</li>
        </ul>
        <p>We do not sell personal information.</p>
      </Section>

      <Section title="5. Data retention and deletion">
        <p>
          We keep your account and extracted charge data while your account is active. You can disconnect Gmail at any time from your dashboard
          or from your <a href="https://myaccount.google.com/permissions" target="_blank" rel="noreferrer" className="text-[#00E676]">Google Account permissions</a>; this immediately revokes our access and we delete your stored Gmail tokens.
          To delete your account and all associated data, email <a href="mailto:support@refundbot.org" className="text-[#00E676]">support@refundbot.org</a> from your account address — we complete deletion within 30 days.
        </p>
      </Section>

      <Section title="6. Security">
        <p>
          Data is encrypted in transit (TLS) and at rest. OAuth tokens are stored encrypted and scoped to the minimum permissions required.
          Sessions use HttpOnly, Secure cookies. Access to production systems is restricted to authorized personnel.
        </p>
      </Section>

      <Section title="7. Your rights">
        <p>
          Depending on where you live (including the EU/UK under GDPR and California under CCPA/CPRA) you may have the right to access, correct,
          export or delete your personal data, and to object to or restrict certain processing. Contact us at the address below to exercise these rights.
        </p>
      </Section>

      <Section title="8. Children">
        <p>The Service is not directed to anyone under 18 and we do not knowingly collect data from children.</p>
      </Section>

      <Section title="9. Changes">
        <p>We may update this policy. We will post the new version here with an updated date and, for material changes, notify you by email.</p>
      </Section>

      <Section title="10. Contact">
        <p>Clawback · <a href="mailto:support@refundbot.org" className="text-[#00E676]">support@refundbot.org</a> · <a href="https://refundbot.org" className="text-[#00E676]">https://refundbot.org</a></p>
      </Section>
    </LegalPage>
  );
}
